/* Refined to 6 strategic steps as requested */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiUserPlus, FiCompass, FiTarget, FiZap, FiAward, FiTrendingUp, FiChevronRight, FiChevronLeft } from 'react-icons/fi';

const steps = [
  {
    id: 'setup',
    title: 'Initialize Core',
    icon: FiUserPlus,
    desc: 'New users setup their neural profile, defining their current skill baseline and learning velocity goals.',
    detail: 'Profile Status: [ONLINE] // User: Alex_Neo // Rank: Recruit'
  },
  {
    id: 'select',
    title: 'Vocation Mapping',
    icon: FiCompass,
    desc: 'The system analyzes market demand and suggests a non-linear skill path nodes.',
    detail: 'Selected Path: Frontend Mastery // Nodes detected: 42'
  },
  {
    id: 'boss',
    title: 'Daily Boss Encounter',
    icon: FiTarget,
    desc: 'A high-stakes logic puzzle appears every 24h. Solving it is the only way to sustain your practice streak.',
    detail: 'Current Boss: Recursive Logic // Reward: 100 XP + Shield'
  },
  {
    id: 'drills',
    title: 'Synaptic Drills',
    icon: FiZap,
    desc: 'Interactive micro-lessons delivered via mobile-first cards for maximum information density.',
    detail: 'Module: Advanced Hooks // Progress: 65% // Efficiency: 4.2ms/node'
  },
  {
    id: 'forge',
    title: 'Achievement Forging',
    icon: FiAward,
    desc: 'Nodes are synthesized into badges. These verified milestones build your public reputation.',
    detail: 'New Badge Minted: [HOOK_MASTER_V1]'
  },
  {
    id: 'ascend',
    title: 'Rank Ascension',
    icon: FiTrendingUp,
    desc: 'Climb the global cohort leaderboard. Higher ranks unlock advanced paths and restricted skill sectors.',
    detail: 'Rank Update: Recuit -> Cyber Knight // Global: Top 5%'
  }
];

export default function WorkflowStepper() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="bg-navy-900 rounded-3xl overflow-hidden shadow-2xl">
      <div className="p-4 md:p-6 border-b border-navy-800 bg-navy-800/20 scrollbar-hide overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {steps.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(idx)}
              className={`py-2 px-4 rounded-xl border text-[10px] font-mono transition-all flex items-center gap-2 ${
                activeStep === idx 
                  ? 'bg-quest-500 border-quest-500 text-navy-950 shadow-glow-green font-bold' 
                  : 'bg-navy-950 border-navy-700 text-gray-500 hover:text-white'
              }`}
            >
              <span className="opacity-50">0{idx + 1}</span>
              <span className="uppercase tracking-wider">{step.title}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 md:p-12 min-h-[320px] flex flex-col md:flex-row gap-8 items-center justify-between">
        <div className="flex-1 max-w-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-navy-800 border border-navy-700 flex items-center justify-center shadow-inner">
                  <SafeIcon icon={steps[activeStep].icon} className="text-2xl text-quest-500" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">{steps[activeStep].title}</h3>
              </div>
              
              <p className="text-gray-400 text-lg leading-relaxed">
                {steps[activeStep].desc}
              </p>
              
              <div className="p-4 bg-navy-950 border-l-4 border-trophy-500 rounded-r-xl font-mono text-xs text-trophy-400">
                <span className="text-gray-600 block mb-1 uppercase tracking-tighter">System Log:</span>
                {steps[activeStep].detail}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex md:flex-col gap-3">
          <button 
            disabled={activeStep === 0}
            onClick={() => setActiveStep(prev => prev - 1)}
            className="w-12 h-12 rounded-full bg-navy-800 border border-navy-700 text-white flex items-center justify-center disabled:opacity-20 hover:bg-navy-700 transition-colors"
          >
            <SafeIcon icon={FiChevronLeft} className="text-2xl" />
          </button>
          <button 
            disabled={activeStep === steps.length - 1}
            onClick={() => setActiveStep(prev => prev + 1)}
            className="w-12 h-12 rounded-full bg-quest-600 border border-quest-500 text-navy-950 flex items-center justify-center disabled:opacity-20 hover:bg-quest-500 transition-colors shadow-glow-green"
          >
            <SafeIcon icon={FiChevronRight} className="text-2xl" />
          </button>
        </div>
      </div>
    </div>
  );
}